import { Controller } from '@hotwired/stimulus';

/**
 * RegularizationPickerController
 *
 * Drives the WPS « Régularisations » and « Pénalités » modals. Panels swap
 * via the `hidden` class (never inline styles), with focus moved to the
 * first field of the panel shown, for keyboard and screen reader users.
 *
 * The same controller drives both modals; `categoryValue`
 * ('adjustment' | 'penalty') drives the total-display sign. Only the
 * Régularisations modal carries `regularizationForm` / `prorataForm`
 * (and only when the statement feeds an active prorata account), so EVERY
 * access to those two targets is guarded by `has…Target`: an unguarded read
 * throws on `connect()` and breaks the Pénalités modal.
 *
 * Targets:
 * - regularizationForm: the contract-regularization form (catalog, manual
 *   and continue panels live inside it). Optional.
 * - prorataForm: the sibling « Régulariser le compte prorata » form
 *   (another resource, another POST). Optional, root carries `hidden` only.
 * - catalogPanel: contract template cards + quantity input + formula
 * - manualPanel: free-form label + amount inputs. Optional.
 * - continuePanel: existing-regularization selector + amount. Optional.
 * - card: a template card (role="radio" + aria-checked)
 * - submitButton: the contract-regularization submit button, disabled until
 *   a valid state. NOT the prorata form's button.
 * - templateInput: hidden contract_regularization_template_id field
 * - quantityInput / quantityRow: quantity input and its wrapper
 * - unitAmount: hidden span carrying the template amount
 * - formulaDisplay / totalDisplay: « × 500 € » and the signed total
 *
 * Values:
 * - category: 'adjustment' | 'penalty'
 *
 * Actions:
 * - showCatalog / back: back to the catalog; re-shows `regularizationForm`,
 *   hides `prorataForm`
 * - showManual / showContinue: switch panel inside `regularizationForm`
 * - showProrata: hides `regularizationForm`, shows `prorataForm` and focuses
 *   its label field (the trigger is an `href="#"` link, default prevented)
 * - selectCard / recomputeFormula: catalog card selection and formula
 *
 * @example
 * <div data-controller="regularization-picker"
 *      data-regularization-picker-category-value="adjustment">
 *   <form data-regularization-picker-target="regularizationForm">
 *     <div data-regularization-picker-target="catalogPanel">
 *       <a href="#" data-action="click->regularization-picker#showProrata">…</a>
 *     </div>
 *   </form>
 *   <form class="hidden" data-regularization-picker-target="prorataForm">
 *     <button type="button" data-action="click->regularization-picker#back">…</button>
 *     <input id="prorata_adjustment_label" …>
 *   </form>
 * </div>
 */
export default class extends Controller {
  static targets = [
    'regularizationForm', // contract-regularization form (Régularisations modal only)
    'prorataForm', // sibling prorata-account form (Régularisations modal only)
    'catalogPanel',
    'manualPanel',
    'continuePanel',
    'card',
    'submitButton',
    'templateInput', // hidden field for contract_regularization_template_id
    'quantityInput',
    'quantityRow', // wrapper around qty input + formula, hidden until a card is selected
    'unitAmount', // hidden span carrying template.amount for formula
    'formulaDisplay', // visible "2 × 500 €" line
    'totalDisplay',
  ];

  static values = {
    category: String, // 'penalty' | 'adjustment'
  };

  connect() {
    this.showCatalog();
    this.disableSubmit();
  }

  // --- panel switching (with focus management for a11y) ---
  // Inputs in hidden panels must have `required` stripped, otherwise
  // HTML5 constraint validation silently blocks form submission from
  // the active panel. _setRequiredInPanel records the original state
  // on `data-original-required` so it can be restored on re-activation.
  showCatalog() {
    this._showRegularizationForm();
    this._show(this.catalogPanelTarget);
    if (this.hasManualPanelTarget) {
      this._hide(this.manualPanelTarget);
      this._setRequiredInPanel(this.manualPanelTarget, false);
    }
    if (this.hasContinuePanelTarget) {
      this._hide(this.continuePanelTarget);
      this._setRequiredInPanel(this.continuePanelTarget, false);
    }
    this._clearCardSelection();
    if (this.hasQuantityRowTarget) {
      this.quantityRowTarget.classList.add('hidden');
    }
    this.disableSubmit();
    this._focusFirstIn(this.catalogPanelTarget);
  }

  showManual() {
    this._showRegularizationForm();
    this._hide(this.catalogPanelTarget);
    if (this.hasManualPanelTarget) {
      this._show(this.manualPanelTarget);
      this._setRequiredInPanel(this.manualPanelTarget, true);
    }
    if (this.hasContinuePanelTarget) {
      this._hide(this.continuePanelTarget);
      this._setRequiredInPanel(this.continuePanelTarget, false);
    }
    this.enableSubmit();
    this._focusFirstIn(this.manualPanelTarget);
  }

  showContinue() {
    this._showRegularizationForm();
    this._hide(this.catalogPanelTarget);
    if (this.hasManualPanelTarget) {
      this._hide(this.manualPanelTarget);
      this._setRequiredInPanel(this.manualPanelTarget, false);
    }
    if (this.hasContinuePanelTarget) {
      this._show(this.continuePanelTarget);
      this._setRequiredInPanel(this.continuePanelTarget, true);
    }
    this.enableSubmit();
    this._focusFirstIn(this.continuePanelTarget);
  }

  // The prorata panel is a SIBLING form: hiding `regularizationForm` hides
  // the catalog/manual/continue panels with it, and the two forms validate
  // and submit independently. The contract panels are reset to the catalog
  // state anyway so `back` lands on a clean catalog.
  showProrata(event) {
    event?.preventDefault();
    if (!this.hasProrataFormTarget) return;

    this._hide(this.catalogPanelTarget);
    if (this.hasManualPanelTarget) {
      this._hide(this.manualPanelTarget);
      this._setRequiredInPanel(this.manualPanelTarget, false);
    }
    if (this.hasContinuePanelTarget) {
      this._hide(this.continuePanelTarget);
      this._setRequiredInPanel(this.continuePanelTarget, false);
    }
    if (this.hasRegularizationFormTarget) {
      this._hide(this.regularizationFormTarget);
    }
    this.disableSubmit();
    this._show(this.prorataFormTarget);

    const labelInput =
      this.prorataFormTarget.querySelector('#prorata_adjustment_label') ||
      this.prorataFormTarget.querySelector('input:not([type="hidden"]), select, textarea');
    labelInput?.focus({ preventScroll: true });
  }

  _showRegularizationForm() {
    if (this.hasRegularizationFormTarget) {
      this._show(this.regularizationFormTarget);
    }
    if (this.hasProrataFormTarget) {
      this._hide(this.prorataFormTarget);
    }
  }

  _setRequiredInPanel(panel, required) {
    if (!panel) return;
    panel.querySelectorAll('input, select, textarea').forEach((el) => {
      if (required) {
        if (el.dataset.originalRequired === 'true' || el.hasAttribute('required')) {
          el.setAttribute('required', 'required');
          el.dataset.originalRequired = 'true';
        }
      } else {
        if (el.hasAttribute('required')) {
          el.dataset.originalRequired = 'true';
        }
        el.removeAttribute('required');
      }
    });
  }

  _focusFirstIn(panel) {
    const focusable = panel.querySelector('input, select, button[role="radio"], textarea');
    focusable?.focus({ preventScroll: true });
  }

  // --- catalog card selection ---
  selectCard(event) {
    const card = event.currentTarget;
    this._clearCardSelection();
    card.dataset.selected = 'true';
    card.setAttribute('aria-checked', 'true');
    if (this.hasQuantityRowTarget) {
      this.quantityRowTarget.classList.remove('hidden');
    }

    if (this.hasTemplateInputTarget) {
      this.templateInputTarget.value = card.dataset.templateId;
    }
    if (this.hasUnitAmountTarget) {
      this.unitAmountTarget.dataset.amount = card.dataset.amount;
    }
    if (this.hasQuantityInputTarget) {
      this.quantityInputTarget.value = 1;
    }
    this.recomputeFormula();
    this.enableSubmit();
  }

  recomputeFormula() {
    if (!this.hasQuantityInputTarget || !this.hasUnitAmountTarget) return;
    const qty = parseFloat(this.quantityInputTarget.value) || 0;
    const unit = parseFloat(this.unitAmountTarget.dataset.amount || '0');
    const total = qty * unit;
    const isPenalty = this.categoryValue === 'penalty';
    const signedUnit = isPenalty ? `-${this._formatEur(unit)}` : this._formatEur(unit);
    const signedTotal = isPenalty ? `-${this._formatEur(total)}` : this._formatEur(total);

    if (this.hasFormulaDisplayTarget) {
      this.formulaDisplayTarget.textContent = `× ${signedUnit}`;
    }
    if (this.hasTotalDisplayTarget) {
      this.totalDisplayTarget.textContent = signedTotal;
    }
  }

  back() {
    this.showCatalog();
  }

  // --- private helpers ---
  _show(el) {
    el.classList.remove('hidden');
  }

  _hide(el) {
    el.classList.add('hidden');
  }

  _clearCardSelection() {
    if (!this.hasCardTarget) return;
    this.cardTargets.forEach((card) => {
      card.dataset.selected = 'false';
      card.setAttribute('aria-checked', 'false');
    });
    if (this.hasTemplateInputTarget) this.templateInputTarget.value = '';
  }

  enableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.removeAttribute('disabled');
    }
  }

  disableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.setAttribute('disabled', 'disabled');
    }
  }

  _formatEur(value) {
    return (
      new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value) + ' €'
    );
  }
}
